import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Phone, UserRound } from 'lucide-react'
import type { LiverActivityItem } from '../lib/liverActivity'

type Props = {
  items: LiverActivityItem[]
  loading: boolean
}

export function LiverActivityTimeline({ items, loading }: Props) {
  if (loading) {
    return <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-gray-400">読み込み中...</div>
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-400">
        まだタイムラインに表示できる活動はありません。
      </div>
    )
  }

  return (
    <ol className="relative space-y-3 pl-6">
      <div className="absolute bottom-4 left-[11px] top-4 w-px bg-gray-200" aria-hidden />
      {items.map((item) => (
        <li key={item.id} className="relative">
          <span className={`absolute -left-6 top-4 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${DOT_TONE[item.tone]}`}>
            {item.kind === 'call' ? <Phone className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}
          </span>
          <article className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${BADGE_TONE[item.tone]}`}>
                    {item.kind === 'call' ? '通話' : '担当'}
                  </span>
                </div>
                {item.meta && <p className="mt-1 text-[11px] text-gray-400">{item.meta}</p>}
              </div>
              <time className="shrink-0 text-[11px] tabular-nums text-gray-400">
                {formatActivityDate(item.occurredAt)}
              </time>
            </div>
            <p className="mt-3 line-clamp-2 text-sm leading-6 text-gray-600">{item.description}</p>
          </article>
        </li>
      ))}
    </ol>
  )
}

const DOT_TONE: Record<LiverActivityItem['tone'], string> = {
  sky: 'bg-sky-500 text-white',
  emerald: 'bg-emerald-500 text-white',
  red: 'bg-red-500 text-white',
  amber: 'bg-amber-500 text-white',
  slate: 'bg-slate-400 text-white',
  violet: 'bg-violet-500 text-white'
}

const BADGE_TONE: Record<LiverActivityItem['tone'], string> = {
  sky: 'bg-sky-50 text-sky-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-100 text-slate-700',
  violet: 'bg-violet-50 text-violet-700'
}

function formatActivityDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return format(date, 'M/d HH:mm', { locale: ja })
}
